import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getScheduleCReport, saveScheduleC, getScheduleCHistory, getSavedScheduleC, getLatestScheduleC, deleteScheduleC, exportScheduleCHistory } from '../lib/api';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  Loader2,
  Building2,
  DollarSign,
  Calculator,
  TrendingUp,
  TrendingDown,
  Car,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  Printer,
  RefreshCw,
  Edit3,
  Save,
  X,
  History,
  Clock,
  Trash2,
  Eye,
  FileCheck,
  FilePen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Editable form field component
function EditableField({ label, value, onChange, lineNumber, isTotal = false, isNegative = false, editMode = false }) {
  const [localValue, setLocalValue] = useState(value || 0);
  
  useEffect(() => {
    setLocalValue(value || 0);
  }, [value]);

  const handleChange = (e) => {
    const newVal = parseFloat(e.target.value) || 0;
    setLocalValue(newVal);
    onChange(newVal);
  };

  return (
    <div className={`flex items-center justify-between py-2 px-3 rounded ${isTotal ? 'bg-[hsl(var(--accent))]/10 font-semibold' : 'hover:bg-muted/30'}`}>
      <div className="flex items-center gap-3">
        {lineNumber && (
          <span className="text-xs font-mono text-muted-foreground w-8">{lineNumber}</span>
        )}
        <span className={isTotal ? 'text-[hsl(var(--accent))]' : ''}>{label}</span>
      </div>
      {editMode && !isTotal ? (
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">$</span>
          <Input
            type="number"
            value={localValue}
            onChange={handleChange}
            className="w-28 h-8 text-right font-mono"
            step="0.01"
          />
        </div>
      ) : (
        <span className={`font-mono ${isNegative ? 'text-red-400' : isTotal ? 'text-[hsl(var(--accent))]' : ''}`}>
          {formatCurrency(value)}
        </span>
      )}
    </div>
  );
}

// Status badge component
function StatusBadge({ status }) {
  const statusConfig = {
    draft: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: FilePen },
    final: { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: FileCheck },
    submitted: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CheckCircle2 },
  };
  
  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;
  
  return (
    <Badge className={config.color}>
      <Icon className="w-3 h-3 mr-1" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

export default function ScheduleCPreview() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [data, setData] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [activeTab, setActiveTab] = useState('preview');
  
  // History state
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadedVersionId, setLoadedVersionId] = useState(null);
  
  // Save dialog state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [versionName, setVersionName] = useState('');
  const [saveStatus, setSaveStatus] = useState('draft');
  const [saveNotes, setSaveNotes] = useState('');

  useEffect(() => {
    fetchScheduleC();
  }, [year]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, year]);

  const fetchScheduleC = async () => {
    setLoading(true);
    try {
      // First check if there's a saved latest version
      const latestRes = await getLatestScheduleC(year);
      if (latestRes.data.exists && latestRes.data.version) {
        const saved = latestRes.data.version;
        setData(saved);
        setEditedData(JSON.parse(JSON.stringify(saved)));
        setLoadedVersionId(saved.id);
        toast.info('Loaded saved Schedule C version');
      } else {
        // Fall back to calculated data
        const response = await getScheduleCReport(year);
        setData(response.data);
        setEditedData(JSON.parse(JSON.stringify(response.data)));
        setLoadedVersionId(null);
      }
    } catch (error) {
      toast.error('Failed to load Schedule C data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await getScheduleCHistory(year, 50);
      setHistory(response.data.versions || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadVersion = async (versionId) => {
    setLoading(true);
    try {
      const response = await getSavedScheduleC(versionId);
      setData(response.data);
      setEditedData(JSON.parse(JSON.stringify(response.data)));
      setLoadedVersionId(versionId);
      setActiveTab('preview');
      toast.success('Version loaded');
    } catch (error) {
      toast.error('Failed to load version');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVersion = async (versionId) => {
    if (!window.confirm('Are you sure you want to delete this version?')) return;
    
    try {
      await deleteScheduleC(versionId);
      setHistory(prev => prev.filter(v => v.id !== versionId));
      toast.success('Version deleted');
      
      // If we deleted the currently loaded version, reload fresh data
      if (versionId === loadedVersionId) {
        setLoadedVersionId(null);
        const response = await getScheduleCReport(year);
        setData(response.data);
        setEditedData(JSON.parse(JSON.stringify(response.data)));
      }
    } catch (error) {
      toast.error('Failed to delete version');
    }
  };

  const loadFreshData = async () => {
    setLoading(true);
    try {
      const response = await getScheduleCReport(year);
      setData(response.data);
      setEditedData(JSON.parse(JSON.stringify(response.data)));
      setLoadedVersionId(null);
      toast.success('Loaded fresh calculated data');
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saveData = {
        ...editedData,
        year,
        version_name: versionName || `Schedule C - ${year}`,
        status: saveStatus,
        notes: saveNotes
      };
      
      const response = await saveScheduleC(saveData);
      setLoadedVersionId(response.data.id);
      setShowSaveDialog(false);
      setVersionName('');
      setSaveNotes('');
      toast.success('Schedule C saved successfully');
      
      // Refresh history if on history tab
      if (activeTab === 'history') {
        fetchHistory();
      }
    } catch (error) {
      toast.error('Failed to save Schedule C');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const exportData = editMode ? editedData : data;
      
      const response = await axios.post(
        `${API_URL}/api/reports/export/schedule-c/custom`,
        exportData,
        { responseType: 'blob', withCredentials: true }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Schedule_C_${year}${editMode ? '_edited' : ''}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Schedule C PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const updatePartI = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      part_i_income: { ...prev.part_i_income, [field]: value }
    }));
  };

  const updatePartII = (lineKey, value) => {
    setEditedData(prev => ({
      ...prev,
      part_ii_expenses: { ...prev.part_ii_expenses, [lineKey]: value }
    }));
  };

  const updateBusinessInfo = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      business_info: { ...prev.business_info, [field]: value }
    }));
  };

  const updateMileage = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      mileage_details: { ...prev.mileage_details, [field]: value }
    }));
  };

  const cancelEdits = () => {
    setEditedData(JSON.parse(JSON.stringify(data)));
    setEditMode(false);
  };

  const recalculateTotals = () => {
    if (!editedData) return;
    
    const partI = editedData.part_i_income || {};
    const partII = editedData.part_ii_expenses || {};
    const otherExp = editedData.other_expenses || [];
    
    const grossIncome = (partI.line_1_gross_receipts || 0) - (partI.line_2_returns_allowances || 0) - (partI.line_4_cost_of_goods_sold || 0);
    const totalExpenses = Object.values(partII).reduce((sum, val) => sum + (val || 0), 0);
    const totalOther = otherExp.reduce((sum, e) => sum + (e.amount || 0), 0);
    const netProfit = grossIncome - totalExpenses - totalOther;
    
    const seBase = netProfit > 0 ? netProfit * 0.9235 : 0;
    const seTax = seBase > 0 ? seBase * 0.153 : 0;
    
    setEditedData(prev => ({
      ...prev,
      part_i_income: {
        ...prev.part_i_income,
        line_3_net_receipts: (partI.line_1_gross_receipts || 0) - (partI.line_2_returns_allowances || 0),
        line_5_gross_profit: grossIncome,
        line_7_gross_income: grossIncome
      },
      totals: {
        total_expenses: totalExpenses,
        total_other_expenses: totalOther,
        net_profit_loss: netProfit
      },
      tax_estimates: {
        self_employment_tax: seTax,
        se_deduction: seTax / 2,
        income_tax_estimates: {
          '10_percent': netProfit > 0 ? netProfit * 0.10 : 0,
          '22_percent': netProfit > 0 ? netProfit * 0.22 : 0,
          '24_percent': netProfit > 0 ? netProfit * 0.24 : 0,
          '32_percent': netProfit > 0 ? netProfit * 0.32 : 0,
        }
      }
    }));
    
    toast.success('Totals recalculated');
  };

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const displayData = editMode ? editedData : data;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--accent))]" />
      </div>
    );
  }

  if (!displayData) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-lg font-medium">Failed to load Schedule C data</p>
        <Button onClick={fetchScheduleC} variant="outline" className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" /> Try Again
        </Button>
      </div>
    );
  }

  const { business_info, part_i_income, part_ii_expenses, mileage_details, totals, tax_estimates, other_expenses } = displayData;

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6 text-[hsl(var(--accent))]" />
              Schedule C Preview
              {editMode && <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 ml-2">Editing</Badge>}
              {loadedVersionId && <Badge variant="outline" className="ml-2">Saved Version</Badge>}
            </h1>
            <p className="text-muted-foreground">Profit or Loss From Business (Sole Proprietorship)</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 mr-2">
            <Switch
              checked={editMode}
              onCheckedChange={setEditMode}
              id="edit-mode"
            />
            <Label htmlFor="edit-mode" className="flex items-center gap-1 cursor-pointer">
              <Edit3 className="w-4 h-4" />
              Edit
            </Label>
          </div>
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))} disabled={editMode}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {editMode && (
            <>
              <Button variant="outline" size="sm" onClick={recalculateTotals}>
                <Calculator className="w-4 h-4 mr-1" /> Recalc
              </Button>
              <Button variant="ghost" size="sm" onClick={cancelEdits}>
                <X className="w-4 h-4" />
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => setShowSaveDialog(true)}>
            <Save className="w-4 h-4 mr-2" /> Save
          </Button>
          <Button onClick={handleExportPDF} disabled={exporting} className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-black">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            PDF
          </Button>
        </div>
      </div>

      {/* Tabs for Preview vs History */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="preview" data-testid="tab-preview">
            <FileText className="w-4 h-4 mr-2" /> Preview
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="w-4 h-4 mr-2" /> History
          </TabsTrigger>
        </TabsList>

        {/* Preview Tab */}
        <TabsContent value="preview" className="space-y-6 mt-6">
          {/* Edit Mode Warning */}
          {editMode && (
            <div className="p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/5">
              <div className="flex items-center gap-2 text-yellow-400">
                <Edit3 className="w-5 h-5" />
                <span className="font-medium">Edit Mode Enabled</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Edit values, click "Recalc" to update totals, then "Save" to store or "PDF" to export.
              </p>
            </div>
          )}

          {/* Loaded Version Info */}
          {loadedVersionId && displayData.version_name && (
            <div className="p-4 rounded-lg border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileCheck className="w-5 h-5 text-[hsl(var(--accent))]" />
                <div>
                  <p className="font-medium">{displayData.version_name}</p>
                  <p className="text-sm text-muted-foreground">
                    Saved {formatDate(displayData.updated_at || displayData.created_at)}
                  </p>
                </div>
                <StatusBadge status={displayData.status || 'draft'} />
              </div>
              <Button variant="outline" size="sm" onClick={loadFreshData}>
                <RefreshCw className="w-4 h-4 mr-2" /> Load Fresh Data
              </Button>
            </div>
          )}

          {/* IRS Form Header */}
          <Card className="card-premium border-2 border-[hsl(var(--accent))]/30">
            <CardContent className="pt-6">
              <div className="text-center mb-4">
                <p className="text-sm text-muted-foreground">Form</p>
                <h2 className="text-3xl font-bold">1040 Schedule C</h2>
                <p className="text-muted-foreground">Department of the Treasury — Internal Revenue Service</p>
                <p className="font-semibold mt-2">Profit or Loss From Business</p>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-6 p-4 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Tax Year</p>
                  <p className="text-xl font-bold text-[hsl(var(--accent))]">{year}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Status</p>
                  {displayData.status ? (
                    <StatusBadge status={displayData.status} />
                  ) : (
                    <Badge className={editMode ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" : "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"}>
                      {editMode ? 'Being Edited' : 'Ready for Review'}
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Business Information */}
          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[hsl(var(--accent))]" />
                Business Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-muted-foreground">Business Name (Line A)</Label>
                    {editMode ? (
                      <Input value={business_info?.name || ''} onChange={(e) => updateBusinessInfo('name', e.target.value)} className="mt-1" />
                    ) : (
                      <p className="font-medium text-lg">{business_info?.name || 'Not specified'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Principal Business (Line B)</Label>
                    {editMode ? (
                      <Input value={business_info?.type || ''} onChange={(e) => updateBusinessInfo('type', e.target.value)} className="mt-1" />
                    ) : (
                      <p className="font-medium">{business_info?.type || 'Sole Proprietorship'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Employer ID Number (Line D)</Label>
                    {editMode ? (
                      <Input value={business_info?.ein || ''} onChange={(e) => updateBusinessInfo('ein', e.target.value)} className="mt-1 font-mono" placeholder="XX-XXXXXXX" />
                    ) : (
                      <p className="font-mono">{business_info?.ein || 'N/A'}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-muted-foreground">Business Address (Line E)</Label>
                    {editMode ? (
                      <div className="space-y-2 mt-1">
                        <Input value={business_info?.address?.line1 || ''} onChange={(e) => updateBusinessInfo('address', {...business_info?.address, line1: e.target.value})} placeholder="Street Address" />
                        <div className="grid grid-cols-3 gap-2">
                          <Input value={business_info?.address?.city || ''} onChange={(e) => updateBusinessInfo('address', {...business_info?.address, city: e.target.value})} placeholder="City" />
                          <Input value={business_info?.address?.state || ''} onChange={(e) => updateBusinessInfo('address', {...business_info?.address, state: e.target.value})} placeholder="State" />
                          <Input value={business_info?.address?.zip || ''} onChange={(e) => updateBusinessInfo('address', {...business_info?.address, zip: e.target.value})} placeholder="ZIP" />
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="font-medium">{business_info?.address?.line1 || 'Not specified'}</p>
                        <p className="font-medium">{business_info?.address?.city && `${business_info.address.city}, `}{business_info?.address?.state} {business_info?.address?.zip}</p>
                      </>
                    )}
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Accounting Method (Line F)</Label>
                    {editMode ? (
                      <Select value={business_info?.accounting_method || 'cash'} onValueChange={(v) => updateBusinessInfo('accounting_method', v)}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="accrual">Accrual</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline" className="capitalize">{business_info?.accounting_method || 'Cash'}</Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Part I - Income */}
          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                Part I — Income
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <EditableField lineNumber="1" label="Gross receipts or sales" value={part_i_income?.line_1_gross_receipts} onChange={(v) => updatePartI('line_1_gross_receipts', v)} editMode={editMode} />
              <EditableField lineNumber="2" label="Returns and allowances" value={part_i_income?.line_2_returns_allowances} onChange={(v) => updatePartI('line_2_returns_allowances', v)} editMode={editMode} isNegative />
              <EditableField lineNumber="3" label="Subtract line 2 from line 1" value={part_i_income?.line_3_net_receipts} isTotal />
              <EditableField lineNumber="4" label="Cost of goods sold (from Part III)" value={part_i_income?.line_4_cost_of_goods_sold} onChange={(v) => updatePartI('line_4_cost_of_goods_sold', v)} editMode={editMode} isNegative />
              <Separator className="my-2" />
              <EditableField lineNumber="5" label="Gross profit" value={part_i_income?.line_5_gross_profit} isTotal />
              <EditableField lineNumber="6" label="Other income" value={part_i_income?.line_6_other_income} onChange={(v) => updatePartI('line_6_other_income', v)} editMode={editMode} />
              <Separator className="my-2" />
              <EditableField lineNumber="7" label="Gross income" value={part_i_income?.line_7_gross_income} isTotal />
            </CardContent>
          </Card>

          {/* Part II - Expenses */}
          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-400" />
                Part II — Expenses
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {Object.entries(part_ii_expenses || {}).length > 0 ? (
                <>
                  {Object.entries(part_ii_expenses).map(([line, amount]) => (
                    <EditableField key={line} label={line} value={amount} onChange={(v) => updatePartII(line, v)} editMode={editMode} isNegative />
                  ))}
                  <Separator className="my-2" />
                  <EditableField label="Total expenses" value={totals?.total_expenses} isTotal isNegative />
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No expenses recorded for {year}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mileage */}
          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="w-5 h-5 text-blue-400" />
                Vehicle Expenses (Line 9)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-muted/30 text-center">
                  {editMode ? (
                    <Input type="number" value={mileage_details?.total_miles || 0} onChange={(e) => updateMileage('total_miles', parseFloat(e.target.value) || 0)} className="text-center text-xl font-bold" />
                  ) : (
                    <p className="text-2xl font-bold">{mileage_details?.total_miles?.toLocaleString() || 0}</p>
                  )}
                  <p className="text-sm text-muted-foreground">Total Miles</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/30 text-center">
                  <p className="text-2xl font-bold">{mileage_details?.business_miles?.toLocaleString() || 0}</p>
                  <p className="text-sm text-muted-foreground">Business Miles</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/30 text-center">
                  <p className="text-2xl font-bold">${mileage_details?.rate_per_mile || 0.67}</p>
                  <p className="text-sm text-muted-foreground">IRS Rate/Mile</p>
                </div>
                <div className="p-4 rounded-lg bg-[hsl(var(--accent))]/10 text-center">
                  <p className="text-2xl font-bold text-[hsl(var(--accent))]">{formatCurrency(mileage_details?.deduction)}</p>
                  <p className="text-sm text-muted-foreground">Mileage Deduction</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Net Profit/Loss */}
          <Card className="card-premium border-2 border-[hsl(var(--accent))]/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-[hsl(var(--accent))]" />
                Net Profit or Loss
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-emerald-500/10 text-center">
                  <p className="text-sm text-muted-foreground">Gross Income</p>
                  <p className="text-2xl font-bold text-emerald-400">{formatCurrency(part_i_income?.line_7_gross_income)}</p>
                </div>
                <div className="p-4 rounded-lg bg-red-500/10 text-center">
                  <p className="text-sm text-muted-foreground">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-400">{formatCurrency((totals?.total_expenses || 0) + (totals?.total_other_expenses || 0))}</p>
                </div>
                <div className={`p-4 rounded-lg text-center ${(totals?.net_profit_loss || 0) >= 0 ? 'bg-[hsl(var(--accent))]/10' : 'bg-red-500/20'}`}>
                  <p className="text-sm text-muted-foreground">Net Profit/Loss</p>
                  <p className={`text-3xl font-bold ${(totals?.net_profit_loss || 0) >= 0 ? 'text-[hsl(var(--accent))]' : 'text-red-400'}`}>
                    {formatCurrency(totals?.net_profit_loss)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tax Estimates */}
          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-yellow-400" />
                Estimated Tax Liability
                <Badge variant="outline" className="ml-2">Estimates Only</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-semibold text-muted-foreground">Self-Employment Tax</h4>
                  <div className="p-4 rounded-lg bg-muted/30 space-y-2">
                    <div className="flex justify-between">
                      <span>SE Tax (15.3%)</span>
                      <span className="font-mono">{formatCurrency(tax_estimates?.self_employment_tax)}</span>
                    </div>
                    <div className="flex justify-between text-emerald-400">
                      <span>Deductible (50%)</span>
                      <span className="font-mono">-{formatCurrency(tax_estimates?.se_deduction)}</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <h4 className="font-semibold text-muted-foreground">Income Tax (by bracket)</h4>
                  <div className="p-4 rounded-lg bg-muted/30 space-y-2">
                    {Object.entries(tax_estimates?.income_tax_estimates || {}).map(([bracket, amount]) => (
                      <div key={bracket} className="flex justify-between">
                        <span>{bracket.replace('_', ' ').replace('percent', '%')}</span>
                        <span className="font-mono">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <History className="w-5 h-5 text-[hsl(var(--accent))]" />
                  Schedule C History - {year}
                </span>
                <div className="flex items-center gap-2">
                  {history.length > 0 && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={async () => {
                          try {
                            const res = await exportScheduleCHistory(year, 'csv');
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `schedule_c_history_${year}.csv`;
                            a.click();
                            toast.success('History exported as CSV');
                          } catch (e) { toast.error('Export failed'); }
                        }}
                      >
                        <Download className="w-4 h-4 mr-1" /> CSV
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={async () => {
                          try {
                            const res = await exportScheduleCHistory(year, 'pdf');
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `schedule_c_history_${year}.pdf`;
                            a.click();
                            toast.success('History exported as PDF');
                          } catch (e) { toast.error('Export failed'); }
                        }}
                      >
                        <Download className="w-4 h-4 mr-1" /> PDF
                      </Button>
                    </>
                  )}
                  <Button variant="outline" size="sm" onClick={fetchHistory}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No saved versions</p>
                  <p className="text-sm">Save your Schedule C to track versions and changes</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((version) => (
                    <div
                      key={version.id}
                      className={`p-4 rounded-lg border ${version.id === loadedVersionId ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5' : 'border-border hover:bg-muted/30'} transition-colors`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{version.version_name}</p>
                              <StatusBadge status={version.status} />
                              {version.is_latest && (
                                <Badge variant="outline" className="text-xs">Latest</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDate(version.created_at)}
                              </span>
                              <span>Net: {formatCurrency(version.totals?.net_profit_loss)}</span>
                            </div>
                            {version.notes && (
                              <p className="text-sm text-muted-foreground mt-1">{version.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => loadVersion(version.id)}
                            disabled={version.id === loadedVersionId}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {version.id === loadedVersionId ? 'Loaded' : 'Load'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteVersion(version.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="w-5 h-5 text-[hsl(var(--accent))]" />
              Save Schedule C
            </DialogTitle>
            <DialogDescription>
              Save this Schedule C version to track changes and access later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Version Name</Label>
              <Input
                value={versionName}
                onChange={(e) => setVersionName(e.target.value)}
                placeholder={`Schedule C - ${year}`}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={saveStatus} onValueChange={setSaveStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">
                    <span className="flex items-center gap-2">
                      <FilePen className="w-4 h-4 text-yellow-400" /> Draft
                    </span>
                  </SelectItem>
                  <SelectItem value="final">
                    <span className="flex items-center gap-2">
                      <FileCheck className="w-4 h-4 text-emerald-400" /> Final
                    </span>
                  </SelectItem>
                  <SelectItem value="submitted">
                    <span className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-400" /> Submitted
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                value={saveNotes}
                onChange={(e) => setSaveNotes(e.target.value)}
                placeholder="Any notes about this version..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-black">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Schedule C
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
